import { API_ENDPOINTS } from '@/config/apiEndpoints';
import type { AssetId, ProjectId, UploadId } from '@/shared/types/common.types';
import { apiClient } from '@/shared/utils/api/apiClient';
import { parseOrThrow } from '@/shared/utils/api/parseResponse';
import type { CompletedPart, PartUrlProvider } from '@/shared/utils/upload/multipartUpload';
import {
  assetSchema,
  partUrlsSchema,
  toSignedPartUrls,
  toStoredAsset,
  toUploadSession,
  uploadSessionSchema,
} from '../api/uploadContracts';
import type { CreateUploadInput, SignedPartUrl, StoredAsset, UploadSession } from '../types/upload.types';

/**
 * Core's side of an upload: opening the session, signing part URLs and completing it.
 * The part bytes themselves go to storage through `uploadParts`, never through Core.
 */
export const uploadService = {
  /**
   * `idempotencyKey` is generated once per attempt to open a session, so a retried request
   * that Core already handled returns the same session instead of a second one.
   */
  async create(
    projectId: ProjectId,
    input: CreateUploadInput,
    idempotencyKey: string
  ): Promise<UploadSession> {
    const payload = await apiClient.post<unknown>(API_ENDPOINTS.uploads.create(projectId), input, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return toUploadSession(parseOrThrow(uploadSessionSchema, payload, 'upload session'));
  },

  /** The session with the parts storage already holds, for resuming. */
  async get(projectId: ProjectId, uploadId: UploadId): Promise<UploadSession> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.uploads.session(projectId, uploadId));
    return toUploadSession(parseOrThrow(uploadSessionSchema, payload, 'upload session'));
  },

  /** Core signs at most 100 parts per call; `uploadParts` asks in batches of that size. */
  async partUrls(
    projectId: ProjectId,
    uploadId: UploadId,
    partNumbers: readonly number[],
    signal?: AbortSignal
  ): Promise<readonly SignedPartUrl[]> {
    const payload = await apiClient.post<unknown>(
      API_ENDPOINTS.uploads.partUrls(projectId, uploadId),
      { partNumbers },
      { signal }
    );
    return toSignedPartUrls(parseOrThrow(partUrlsSchema, payload, 'part URLs'));
  },

  /** The engine's URL source for one session. */
  partUrlProvider(projectId: ProjectId, uploadId: UploadId): PartUrlProvider {
    return (partNumbers, signal) => uploadService.partUrls(projectId, uploadId, partNumbers, signal);
  },

  /** Answers `202` with the asset, which verification then moves to `Verified` or `Rejected`. */
  async complete(
    projectId: ProjectId,
    uploadId: UploadId,
    parts: readonly CompletedPart[]
  ): Promise<StoredAsset> {
    const payload = await apiClient.post<unknown>(API_ENDPOINTS.uploads.complete(projectId, uploadId), {
      parts,
    });
    return toStoredAsset(parseOrThrow(assetSchema, payload, 'completed upload'));
  },

  /** Cancels the upload; storage discards the parts it holds. */
  async abort(projectId: ProjectId, uploadId: UploadId): Promise<void> {
    await apiClient.delete<unknown>(API_ENDPOINTS.uploads.session(projectId, uploadId));
  },

  async asset(projectId: ProjectId, assetId: AssetId): Promise<StoredAsset> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.uploads.asset(projectId, assetId));
    return toStoredAsset(parseOrThrow(assetSchema, payload, 'asset'));
  },
};
